import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '@/config';
import storage from '@/utils/storage';
import { useAppContext } from '@/context/AppContext';
import { EventSourcePolyfill } from 'event-source-polyfill';

export const usePodStatus = (enabled: boolean) => {
  const [allPodsSSE, setAllPods] = useState<any[]>([]);
  const [access_token, setAccessToken] = useState(storage.getToken());
  // Use refs to hold the current EventSource and timers
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { xTeamId: xTeamIdFromTeamApi } = useAppContext();

  // Update access token when it changes in storage
  useEffect(() => {
    const currentToken = storage.getToken();
    if (currentToken !== access_token) {
      setAccessToken(currentToken);
    }
  }, [access_token]);

  const establishSSEConnection = useCallback(() => {
    const teamData = storage.getTeam();
    const xTeamIdFromLocalStorage = teamData ? teamData?._id : null;
    const xTeamId = xTeamIdFromTeamApi || xTeamIdFromLocalStorage;
    if (!enabled || !access_token || !xTeamId) return;

    // Close any existing connection using the ref
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = `${API_URL}/v2/pod/status/stream`;
    const newEventSource = new EventSourcePolyfill(url, {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'X-Team': xTeamId,
      },
      withCredentials: false,
    });
    eventSourceRef.current = newEventSource;

    newEventSource.onopen = () => {
      // Clear any pending reconnect timer on a successful connection
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    newEventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setAllPods((prevPods) => {
          const existingIndex = prevPods.findIndex((pod) => pod._id === data._id);
          if (existingIndex !== -1) {
            const updatedPods = [...prevPods];
            updatedPods[existingIndex] = data;
            return updatedPods;
          }
          return [...prevPods, data];
        });
      } catch (err) {
        // Optionally log or ignore non-JSON messages
        // console.warn('Non-JSON SSE message:', event.data);
      }
    };

    newEventSource.onerror = () => {
      newEventSource.close();
      // Schedule a reconnect after 5 seconds
      reconnectTimerRef.current = setTimeout(establishSSEConnection, 5000);
    };
  }, [enabled, access_token, xTeamIdFromTeamApi]);

  useEffect(() => {
    if (enabled) {
      establishSSEConnection();
    } else {
      // On disable, clean up the connection and timers
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    }

    return () => {
      // Cleanup on unmount or dependency change
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [enabled, access_token, xTeamIdFromTeamApi, establishSSEConnection]);

  const terminateSSEConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
  };

  return { allPodsSSE, establishSSEConnection, terminateSSEConnection };
};
